import React, { useState } from 'react';
import { Table, Tag, Button, Space, Tooltip, Modal, Image, Switch } from 'antd';
import { EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FixtureType, FixtureTableProps, FixtureTableColumnKey, SortOrder } from '../../../types/fixture';
import LazyImage from '../../../components/common/LazyImage';

const FixtureTable: React.FC<FixtureTableProps> = ({
  data,
  pagination,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onPageChange,
  onSortChange,
  onView,
  onEdit,
  onDelete,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; record: FixtureType | null }>({
    visible: false,
    record: null,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'green';
      case 'Draft':
        return 'orange';
      case 'Inactive':
        return 'red';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'Active':
        return t('fixture.statusText.Active');
      case 'Draft':
        return t('fixture.statusText.Draft');
      case 'Inactive':
        return t('fixture.statusText.Inactive');
      default:
        return status;
    }
  };

  const formatDimensions = (dimensions: any) => {
    if (!dimensions) return '-';
    return `${dimensions.width}×${dimensions.height}×${dimensions.depth} ${dimensions.unit}`;
  };

  const showDeleteConfirm = (record: FixtureType) => {
    setDeleteModal({
      visible: true,
      record,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.record) {
      onDelete(deleteModal.record.id);
      setDeleteModal({
        visible: false,
        record: null,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      visible: false,
      record: null,
    });
  };

  const columns = [
    {
      title: t('fixture.image'),
      dataIndex: 'imageUrl',
      key: 'image' as FixtureTableColumnKey,
      width: 80,
      render: (imageUrl: string, record: FixtureType) => {
        return <Image
          src={imageUrl}
          alt={``}
          width={60}
          height={45}
          className="fixture-table-image"
        />
      },
    },
    {
      title: t('fixture.typeCode'),
      dataIndex: 'typeCode',
      key: 'typeCode' as FixtureTableColumnKey,
      // sorter: true,
      width: 130,
    },
    {
      title: t('fixture.typeName'),
      dataIndex: 'typeName',
      key: 'typeName' as FixtureTableColumnKey,
      // sorter: true,
      width: 180,
    },
    {
      title: t('fixture.dimensions'),
      dataIndex: 'dimensions',
      key: 'dimensions' as FixtureTableColumnKey,
      width: 150,
      render: (dimensions: any) => (
        <span className="fixture-dimensions">
          {formatDimensions(dimensions)}
        </span>
      ),
    },
    {
      title: t('fixture.holeCount'),
      dataIndex: 'holeCount',
      key: 'holeCount' as FixtureTableColumnKey,
      width: 100,
      render: (holeCount: number) => (
        <span>{holeCount}</span>
      ),
    },
    {
      title: t('fixture.supportHold'),
      dataIndex: 'supportHold',
      key: 'supportHold' as FixtureTableColumnKey,
      width: 120,
      render: (supportHold: boolean) => (
        <Switch 
          checked={supportHold} 
          checkedChildren={t('common.yes')} 
          unCheckedChildren={t('common.no')} 
          disabled 
        />
      ),
    },
    {
      title: t('fixture.crossBeamCount'),
      dataIndex: 'crossBeamCount',
      key: 'crossBeamCount' as FixtureTableColumnKey,
      width: 120,
      render: (crossBeamCount: number) => (
        <span>{crossBeamCount}</span>
      ),
    },
    {
      title: t('fixture.status'),
      dataIndex: 'status',
      key: 'status' as FixtureTableColumnKey,
      // sorter: true,
      width: 100,
      render: (status: string) => (
        <Tag color={getStatusColor(status)} className="fixture-status-tag">
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: t('common.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate' as FixtureTableColumnKey,
      // sorter: true,
      width: 160,
      render: (date: Date) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: t('common.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified' as FixtureTableColumnKey,
      // sorter: true,
      width: 160,
      render: (date: Date) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: t('common.actions'),
      key: 'actions' as FixtureTableColumnKey,
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: FixtureType) => (
        <Space size="small" className="table-action-column">
          <Tooltip title={t('common.view')}>
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => onView(record.id)}
            />
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => onEdit(record.id)}
            />
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => showDeleteConfirm(record)}
              disabled={record.status !== 'Draft'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
    getCheckboxProps: (record: FixtureType) => ({
      disabled: false,
      name: record.id,
    }),
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    if (sorter.field && sorter.order) {
      const order: SortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
      onSortChange(sorter.field as FixtureTableColumnKey, order);
    }
  };

  return (
    <>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.totalCount,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            t('common.paginationTotal', { start: range[0], end: range[1], total }),
          onChange: onPageChange,
          onShowSizeChange: onPageChange,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1300, y: 500 }}
        size="middle"
      />
      <Modal
        title={t('common.deleteConfirm')}
        open={deleteModal.visible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText={t('common.confirm')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true }}
      >
        <p>{t('fixture.deleteConfirmMessage')}</p>
        {deleteModal.record && (
          <p>
            <strong>{deleteModal.record.typeName}</strong> ({deleteModal.record.typeCode})
          </p>
        )}
      </Modal>
    </>
  );
};

export default FixtureTable;